import {
    type Notification,
    notificationReadPath,
    notificationSchema,
    notificationsPath,
    type Photo,
    photoPath,
    photoSchema,
    photosPath,
} from '@baby-tracker/contracts';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiGet, apiRequest, apiUpload } from '../lib/api-client.js';
import { queryKeys } from '../lib/query-keys.js';

export interface PhotoUploadInput {
    file: Blob;
    contentType: string;
    childId: string;
    caption?: string | undefined;
    width?: number | undefined;
    height?: number | undefined;
}

function uploadPath(familyId: string, input: PhotoUploadInput): string {
    const params = new URLSearchParams({ childId: input.childId });
    if (input.caption !== undefined && input.caption.length > 0) {
        params.set('caption', input.caption);
    }
    if (input.width !== undefined) {
        params.set('width', String(input.width));
    }
    if (input.height !== undefined) {
        params.set('height', String(input.height));
    }
    return `${photosPath(familyId)}?${params.toString()}`;
}

export function usePhotos(
    familyId: string | undefined,
    childId: string | undefined,
): UseQueryResult<Photo[]> {
    return useQuery({
        queryKey: queryKeys.photos(familyId ?? '', childId ?? ''),
        enabled: familyId !== undefined && childId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(`${photosPath(familyId ?? '')}?childId=${childId ?? ''}`, photoSchema.array(), {
                signal,
            }),
    });
}

export function useUploadPhoto(
    familyId: string,
): UseMutationResult<Photo, Error, PhotoUploadInput> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: PhotoUploadInput) =>
            apiUpload(uploadPath(familyId, input), input.file, input.contentType, photoSchema),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['photos', familyId] });
        },
    });
}

export function useDeletePhoto(familyId: string): UseMutationResult<Photo, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (photoId: string) => apiRequest('DELETE', photoPath(photoId), photoSchema),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['photos', familyId] });
            await queryClient.invalidateQueries({ queryKey: ['milestones', familyId] });
        },
    });
}

export function useNotifications(familyId: string | undefined): UseQueryResult<Notification[]> {
    return useQuery({
        queryKey: queryKeys.notifications(familyId ?? ''),
        enabled: familyId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(notificationsPath(familyId ?? ''), notificationSchema.array(), { signal }),
    });
}

export function useMarkNotificationRead(
    familyId: string,
): UseMutationResult<Notification, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (notificationId: string) =>
            apiRequest('POST', notificationReadPath(notificationId), notificationSchema, {
                body: {},
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.notifications(familyId) });
        },
    });
}
